import { Copy, Info, QrCodeIcon, UserRound } from "lucide-react";
import React from "react";
import { UseFormReturn } from "react-hook-form";
import QRCode from "react-qr-code";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FloatingLabelInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Switch,
  toast,
  UploadAvatar,
} from "@/components";
import { useI18n } from "@/context/i18n";

import { IMfaKey, TUpdateProfileRequest } from "../../settings.types";
interface IProfileCardProps {
  profileForm: UseFormReturn<TUpdateProfileRequest>;
  file?: File;
  mfaMethodOptions: { value: string; label: string }[];
  fileInputRef: React.RefObject<HTMLInputElement>;
  onCropComplete: (croppedImage: File) => void;
  onSubmitProfile: (data: TUpdateProfileRequest) => Promise<void>;
  mfaEnabled?: number;
  mfaKey?: IMfaKey;
  isQrCodeVisible: boolean;
  onCloseQrCode: () => void;
  personTypeOptions: { value: string; label: string }[];
  personType: string;
}

export const ProfileCard: React.FC<IProfileCardProps> = ({
  profileForm,
  file,
  fileInputRef,
  onCropComplete,
  onSubmitProfile,
  mfaEnabled,
  mfaMethodOptions,
  isQrCodeVisible,
  onCloseQrCode,
  mfaKey,
  personTypeOptions,
  personType,
}) => {
  const { t } = useI18n();

  return (
    <Card className="bg-background w-full">
      <CardHeader className="flex flex-row items-center gap-4">
        <UserRound className="w-8 h-8" />
        <CardTitle>{t("settings.profile.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...profileForm}>
          <form
            onSubmit={profileForm.handleSubmit(onSubmitProfile)}
            className="space-y-4 border-t pt-4"
          >
            <FormField
              control={profileForm.control}
              name="file"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <UploadAvatar
                      file={file}
                      fileInputRef={fileInputRef}
                      onCropComplete={onCropComplete}
                      field={field}
                      fallbackName={profileForm
                        .getValues("name")
                        ?.split(" ")
                        .map((name) => name.charAt(0))
                        .join("")}
                      imageSrc={profileForm.getValues("picturePath")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={profileForm.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FloatingLabelInput
                      label={t("settings.profile.fullName")}
                      placeholder={t("settings.profile.fullName")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={profileForm.control}
              name="whatsapp"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FloatingLabelInput
                      label="Whatsapp"
                      placeholder="Whatsapp"
                      mask="(99)99999-9999"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={profileForm.control}
              name="personType"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Combobox
                      label={t("settings.profile.personType")}
                      options={personTypeOptions}
                      value={field.value || undefined}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      placeholder={t("settings.profile.personType")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {personType && (
              <FormField
                control={profileForm.control}
                name="document"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={personType === "COMPANY" ? "CNPJ" : "CPF"}
                        placeholder={personType === "COMPANY" ? "CNPJ" : "CPF"}
                        mask={
                          personType === "COMPANY"
                            ? "99.999.999/9999-99"
                            : "999.999.999-99"
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={profileForm.control}
              name="mfaEnabled"
              render={({ field }) => (
                <div className="space-y-4">
                  <div className="border-t mt-6" />
                  <div className="font-medium mb-4 block">
                    {t("settings.profile.securityTitle")}
                  </div>
                  <FormItem>
                    <FormControl>
                      <div className="flex items-center justify-between p-4 border rounded-md">
                        <div className="space-y-0.5">
                          <div className="font-medium">
                            {t("settings.profile.mfaTitle")}
                          </div>
                          <div className="text-sm text-muted-foreground text-wrap">
                            {t("settings.profile.mfaDescription")}
                          </div>
                        </div>
                        <Switch
                          checked={field.value === 1}
                          onCheckedChange={(checked) => {
                            field.onChange(checked ? 1 : 0);
                            profileForm.setValue("mfaMethod", null);
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
            {mfaEnabled ? (
              <div className="flex flex-row items-center gap-4">
                <div className="w-11/12">
                  <FormField
                    control={profileForm.control}
                    name="mfaMethod"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Combobox
                            label={t("settings.profile.mfaMethod")}
                            placeholder={t("settings.profile.mfaMethod")}
                            options={mfaMethodOptions}
                            value={field.value || undefined}
                            onChange={(value) => {
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="w-1/12">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={(e) => {
                      e.preventDefault();
                      onCloseQrCode();
                    }}
                    disabled={profileForm.getValues("mfaMethod") !== "APP"}
                  >
                    <QrCodeIcon className="w-6 h-6" />
                  </Button>
                </div>
              </div>
            ) : null}
            <Button type="submit" className="w-full mt-4">
              {t("generic.save")}
            </Button>
          </form>
        </Form>
      </CardContent>
      <Dialog open={isQrCodeVisible} onOpenChange={onCloseQrCode}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("settings.profile.enableMfa")}</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {t("settings.profile.scanQrCodeOrCopySecret")}
          </DialogDescription>
          <div className="flex flex-col justify-center items-center gap-4 cursor-pointer">
            {mfaKey?.url && <QRCode value={mfaKey.url} />}
            <p className="text-sm text-muted-foreground">
              {t("generic.or").toUpperCase()}
            </p>
            <div
              className="flex flex-row items-center gap-2"
              title={t("generic.copy")}
              onClick={() => {
                navigator.clipboard.writeText(mfaKey?.secret || "");
                toast({
                  title: t("generic.success"),
                  description: t("settings.profile.mfaSecretCopied"),
                });
              }}
            >
              <p className="font-semibold text-muted-foreground">
                {mfaKey?.secret}
              </p>
              <Copy className="w-4 h-4 cursor-pointer" />
            </div>

            <div className="flex flex-row items-center gap-2">
              <Info className="w-6 h-4" />
              <p className="text-sm text-muted-foreground">
                {t("settings.profile.mfaKeyReminder")}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
